import PageErrorBoundary from './components/PageErrorBoundary';
import CalendarReminderGate from './components/CalendarReminderGate';
import { useEffect, lazy, Suspense, Fragment } from 'react';
import "@/App.css";
import AiCostDialog from './components/AiCostDialog';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Lock } from 'lucide-react';
import { I18nProvider, LOCALES, useI18n } from './i18n';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LivingProvider } from './contexts/LivingContext';
import LivingBackdrop from './components/living/LivingBackdrop';
import AmbientSync from './components/living/AmbientSync';
import { installTouchFx } from './lib/fx';
import { installScrollCalm } from './lib/scrollCalm';
import AuthCallback from './components/AuthCallback';
import BottomNav from './components/BottomNav';
import GuestBanner from './components/GuestBanner';
import { isEdenSandbox } from './lib/eden/runtime';

import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MinHave from './pages/MinHave';
import Opgaver from './pages/Opgaver';
import Havedoktor from './pages/Havedoktor';
import Opdag from './pages/Opdag';
import Kalender from './pages/Kalender';
import Profil from './pages/Profil';
import Premium from './pages/Premium';
import WeeklyPlan from './pages/WeeklyPlan';
import Privacy from './pages/Privacy';
import PurchaseTerms from './pages/PurchaseTerms';
import DeleteAccount from './pages/DeleteAccount';
import Mushrooms from './pages/Mushrooms';
import MushroomIdentify from './pages/MushroomIdentify';
import QuickIdentify from './pages/QuickIdentify';
import OfflineMode from './pages/OfflineMode';
import OfflineNotice from './components/OfflineNotice';
import PhoneTheme from './pages/PhoneTheme';
import Foraging from './pages/Foraging';
import Achievements from './pages/Achievements';
import Leaderboard from './pages/Leaderboard';
import BedLayout from './pages/BedLayout';
import { CommunityFeed, CommunityBedDetail } from './pages/Community';
const EdenShowcases = lazy(() => import('./pages/EdenShowcases'));
import { MarketplaceFeed, ListingDetail, NewListing } from './pages/Marketplace';
import AdminModeration from './pages/AdminModeration';
import AdminStats from './pages/AdminStats';
import AdminLinks from './pages/AdminLinks';
import AdminStudio from './pages/AdminStudio';
import PromotionStudio from './pages/PromotionStudio';
import WorkHub from './pages/WorkHub';
import DesktopHome from './pages/DesktopHome';
import MagicLinkConsume, { VerifyEmailConsume } from './pages/MagicLinkConsume';
import InstallGuide from './pages/InstallGuide';
import Help from './pages/Help';
import Harvest from './pages/Harvest';
const Recipes = lazy(() => import('./pages/Recipes'));
const Shed = lazy(() => import('./pages/shed/Shed'));
import FindsMap from './pages/FindsMap';
import CookieBanner from './components/CookieBanner';
import PageCanonical from './components/PageCanonical';
import LanguageNudge from './components/LanguageNudge';
import UpdateNotifier from './components/UpdateNotifier';
import NotificationGate from './components/NotificationGate';
import TutorialModal from './components/TutorialModal';
import DeepLinkHandler from './components/DeepLinkHandler';
import ErrorMonitor from './components/ErrorMonitor';

// Eden — the companion game. Lazy on purpose: the game bundle and its atlas
// must never touch app start-up time. Reached from the Dashboard card
// (Addendum 31); before that it was routed but linked from nowhere, so the
// only way in was typing the URL.
const Eden = lazy(() => import('./pages/Eden'));

// Debug-only landing route. Set REACT_APP_START_ROUTE=/eden when building the
// Eden test APK so the app opens straight into the game. Unset in every normal
// build, which makes the branch below dead code after minification.
const START_ROUTE = process.env.REACT_APP_START_ROUTE || '';

// Re-keys on every route so each page "grows" in. Fill mode is `backwards`,
// so no transform lingers to trap position:fixed modals inside the page.
function PageTransition({ children }) {
    const location = useLocation();
    return <div key={location.pathname} className="page-enter">{children}</div>;
}

function ProtectedShell({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!loading && !user && !location.hash?.includes('session_id=')) {
            navigate(navigator.onLine === false ? '/offline' : '/login', { replace: true });
        }
    }, [loading, user, navigate, location]);

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-brand-ink2">
                <div className="w-10 h-10 rounded-full border-2 border-brand-green border-t-transparent animate-spin" />
                <div className="font-serif">Loading…</div>
            </div>
        );
    }
    if (!user) return null;
    return (
        <>
            <PageTransition><PageErrorBoundary resetKey={location.pathname}>{children}</PageErrorBoundary></PageTransition>
            <BottomNav />
            <TutorialModal />
            <NotificationGate />
        </>
    );
}

// Public pages: viewable WITHOUT login (SEO/crawling). Shows a signup banner
// for anonymous visitors. Actions still require auth (backend + CTA toast).
function PublicShell({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    return (
        <>
            {!loading && !user && <GuestBanner />}
            <PageTransition><PageErrorBoundary resetKey={location.pathname}>{children}</PageErrorBoundary></PageTransition>
            <BottomNav />
            {user && (
                <>
                    <TutorialModal />
                    <NotificationGate />
                </>
            )}
        </>
    );
}

function RegisteredShell({ children }) {
    const { user } = useAuth();
    const navigate = useNavigate();

    if (user?.is_guest) {
        return (
            <div className="min-h-screen bg-brand-bg botanical-noise flex flex-col items-center justify-center p-6 text-center">
                <div className="max-w-md w-full bg-brand-card border border-brand-border rounded-3xl p-6 sm:p-8 shadow-sm">
                    <div className="w-16 h-16 rounded-2xl bg-brand-green/15 text-brand-green flex items-center justify-center mx-auto mb-5">
                        <Lock size={28} strokeWidth={1.8} />
                    </div>
                    <h2 className="font-serif text-2xl text-brand-ink mb-3">Create a free profile</h2>
                    <p className="text-sm text-brand-ink2 mb-6 leading-relaxed">
                        This feature is available to registered users. Create a free profile or sign in to continue.
                    </p>
                    <div className="space-y-3">
                        <button
                            data-testid="btn-login-wall-register"
                            onClick={() => navigate('/register')}
                            className="w-full bg-brand-green hover:bg-brand-greenDark text-white py-3 rounded-full font-medium shadow-sm transition-all"
                        >
                            Create profile
                        </button>
                        <button
                            data-testid="btn-login-wall-login"
                            onClick={() => navigate('/login')}
                            className="w-full bg-brand-card hover:bg-white border border-brand-border text-brand-ink py-3 rounded-full font-medium transition-all"
                        >
                            Log ind
                        </button>
                        <button
                            data-testid="btn-login-wall-back"
                            onClick={() => navigate('/dashboard')}
                            className="w-full text-xs text-brand-ink3 hover:text-brand-ink font-medium uppercase tracking-widest pt-2"
                        >
                            Back to home
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return children;
}

// "/" → dashboard when logged in, otherwise the public discover page (SEO-friendly).
// Eden's own guard (Addendum 31). The game needs a signed-in player -- every
// one of its endpoints calls `require_user`, so an anonymous visitor would get
// a game whose every request 401s, which reads as broken rather than as an
// invitation. It deliberately does NOT use `ProtectedShell`: that also renders
// `BottomNav`, which is fixed to the bottom of the viewport, and Eden draws a
// full-bleed canvas underneath. Covering the plot the player is tapping is a
// "functionally broken" failure, and this sandbox cannot see the result -- so
// the standing rule picks the variant whose worst case is milder. Eden carries
// its own way back instead.
function EdenShell({ children }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const sandbox = isEdenSandbox();

    useEffect(() => {
        if (!sandbox && !loading && !user && !location.hash?.includes('session_id=')) {
            navigate('/login', { replace: true });
        }
    }, [sandbox, loading, user, navigate, location]);

    if (!sandbox && (loading || !user)) return <EdenLoading />;
    return <Fragment key={user?.user_id || 'sandbox'}>{children}</Fragment>;
}

function EdenLoading() {
    const { t } = useI18n();
    return <div role="status" className="min-h-screen grid place-items-center bg-brand-bg text-brand-ink">{t('eden.loading')}</div>;
}

function RootRedirect() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (window.growDailyDesktop?.mode === 'local' || window.location.protocol === 'file:') return <DesktopHome />;
    if (navigator.onLine === false) return <Navigate to="/offline" replace />;
    return <Navigate to={user ? '/dashboard' : '/discover'} replace />;
}

function LocalizedDiscover() {
    const { lang } = useParams();
    const { setLocale } = useI18n();
    const supported = LOCALES.some((item) => item.code.toLowerCase() === String(lang || '').toLowerCase());
    useEffect(() => {
        if (!supported) return;
        setLocale(lang);
        document.documentElement.lang = lang;
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.href = `https://growdaily.fun/${lang}/discover`;
    }, [lang, setLocale, supported]);
    if (!supported) return <Navigate to="/discover" replace />;
    return <PublicShell><Opdag /></PublicShell>;
}

// Sends SPA page_view events to Google Analytics on every route change.
function AnalyticsPageViews() {
    const location = useLocation();
    useEffect(() => {
        // Path only: query strings can carry one-time login tokens (?t=…)
        try { if (window.gtag) window.gtag('event', 'page_view', { page_path: location.pathname }); } catch { /* ignore */ }
    }, [location]);
    return null;
}

function AppRouter() {
    const location = useLocation();
    if (window.location.protocol === 'file:' || window.location.hash.includes('/admin/promotion')) return <DesktopHome />;
    // CRITICAL: synchronous check for OAuth callback
    if (location.hash?.includes('session_id=')) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={START_ROUTE ? <Navigate to={START_ROUTE} replace /> : <RootRedirect />} />
            <Route path="/eden" element={<EdenShell><Suspense fallback={<EdenLoading />}><Eden /></Suspense></EdenShell>} />
            {/* Lyn-ID -- the one-tap identifier. Deliberately shell-less: BottomNav
                is fixed to the bottom of the viewport and would sit on top of the
                shutter, and speed is the entire feature, so nothing gets to stand
                in front of the camera. A signed-out visitor still gets a working
                viewfinder; the endpoint answers with a sign-in state, which is a
                far better first impression than a login wall from a lock screen.
                Imported eagerly, not lazily: a chunk fetch before the camera
                opens is exactly the delay this screen exists to remove. */}
            <Route path="/id" element={<QuickIdentify />} />
            <Route path="/offline" element={<PublicShell><OfflineMode /></PublicShell>} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/dashboard" element={<ProtectedShell><Dashboard /></ProtectedShell>} />
            <Route path="/my-garden" element={<ProtectedShell><MinHave /></ProtectedShell>} />
            <Route path="/tasks" element={<ProtectedShell><Opgaver /></ProtectedShell>} />
            <Route path="/garden-doctor" element={<PublicShell><Havedoktor /></PublicShell>} />
            <Route path="/discover" element={<PublicShell><Opdag /></PublicShell>} />
            <Route path="/:lang/discover" element={<LocalizedDiscover />} />
            <Route path="/calendar" element={<PublicShell><Kalender /></PublicShell>} />
            <Route path="/profile" element={<ProtectedShell><Profil /></ProtectedShell>} />
            <Route path="/help" element={<PublicShell><Help /></PublicShell>} />
            <Route path="/premium" element={<PublicShell><Premium /></PublicShell>} />
            <Route path="/premium/success" element={<ProtectedShell><Premium /></ProtectedShell>} />
            <Route path="/weekly-plan" element={<ProtectedShell><RegisteredShell><WeeklyPlan /></RegisteredShell></ProtectedShell>} />
            <Route path="/harvest" element={<ProtectedShell><Harvest /></ProtectedShell>} />
            <Route path="/recipes" element={<PublicShell><Suspense fallback={<EdenLoading />}><Recipes /></Suspense></PublicShell>} />
            <Route path="/shed/*" element={<PublicShell><Suspense fallback={<EdenLoading />}><Shed /></Suspense></PublicShell>} />
            <Route path="/finds-map" element={<PublicShell><FindsMap /></PublicShell>} />
            <Route path="/mushrooms" element={<PublicShell><Mushrooms /></PublicShell>} />
            <Route path="/mushroom-identify" element={<PublicShell><MushroomIdentify /></PublicShell>} />
            <Route path="/phone-theme" element={<PublicShell><PhoneTheme /></PublicShell>} />
            <Route path="/foraging" element={<PublicShell><Foraging /></PublicShell>} />
            <Route path="/achievements" element={<ProtectedShell><Achievements /></ProtectedShell>} />
            <Route path="/leaderboard" element={<PublicShell><Leaderboard /></PublicShell>} />
            <Route path="/bed-layout" element={<ProtectedShell><BedLayout /></ProtectedShell>} />
          <Route path="/community" element={<PublicShell><CommunityFeed /></PublicShell>} />
          <Route path="/community/eden" element={<PublicShell><Suspense fallback={null}><EdenShowcases /></Suspense></PublicShell>} />
          <Route path="/community/eden/:token" element={<PublicShell><Suspense fallback={null}><EdenShowcases /></Suspense></PublicShell>} />
            <Route path="/community/bed/:token" element={<PublicShell><CommunityBedDetail /></PublicShell>} />
            <Route path="/marketplace" element={<PublicShell><MarketplaceFeed /></PublicShell>} />
            <Route path="/marketplace/new" element={<ProtectedShell><RegisteredShell><NewListing /></RegisteredShell></ProtectedShell>} />
            <Route path="/marketplace/:id" element={<PublicShell><ListingDetail /></PublicShell>} />
            <Route path="/admin/moderation" element={<ProtectedShell><AdminModeration /></ProtectedShell>} />
            <Route path="/admin/stats" element={<ProtectedShell><AdminStats /></ProtectedShell>} />
            <Route path="/admin/links" element={<ProtectedShell><AdminLinks /></ProtectedShell>} />
            <Route path="/admin/studio" element={<ProtectedShell><AdminStudio /></ProtectedShell>} />
            <Route path="/admin/promotion" element={<ProtectedShell><PromotionStudio /></ProtectedShell>} />
            <Route path="/admin/workhub" element={<ProtectedShell><WorkHub /></ProtectedShell>} />
            <Route path="/auth/magic" element={<MagicLinkConsume />} />
            <Route path="/auth/verify-email" element={<VerifyEmailConsume />} />
            <Route path="/installer" element={<InstallGuide />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/purchase-terms" element={<PurchaseTerms />} />
            <Route path="/delete-account" element={<DeleteAccount />} />
            {/* Backward-compatible redirects for existing shared/bookmarked Danish URLs. */}
            <Route path="/min-have" element={<Navigate to="/my-garden" replace />} />
            <Route path="/opgaver" element={<Navigate to="/tasks" replace />} />
            <Route path="/havedoktor" element={<Navigate to="/garden-doctor" replace />} />
            <Route path="/opdag" element={<Navigate to="/discover" replace />} />
            <Route path="/kalender" element={<Navigate to="/calendar" replace />} />
            <Route path="/profil" element={<Navigate to="/profile" replace />} />
            <Route path="/uge-plan" element={<Navigate to="/weekly-plan" replace />} />
            <Route path="/svampe" element={<Navigate to="/mushrooms" replace />} />
            <Route path="/svampe-id" element={<Navigate to="/mushroom-identify" replace />} />
            <Route path="/vild-guide" element={<Navigate to="/foraging" replace />} />
            <Route path="/privatliv" element={<Navigate to="/privacy" replace />} />
            <Route path="/slet-konto" element={<Navigate to="/delete-account" replace />} />
            <Route path="/skuret" element={<Navigate to="/shed" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
    );
}

function App() {
    // Register service worker for PWA (production AND for push in preview)
    useEffect(() => {
        if ('serviceWorker' in navigator) {
            const register = () => {
                navigator.serviceWorker.register('/sw.js').catch(() => {});
            };
            if (document.readyState === 'complete') register();
            else window.addEventListener('load', register, { once: true });
        }
        // Capacitor native push (no-op on web)
        import('./lib/nativePush').then(({ registerNativePush }) => registerNativePush()).catch(() => {});
        // Fix Android WebView grey-screen-on-resume
        import('./lib/resumeFix').then(({ initResumeFix }) => initResumeFix()).catch(() => {});
        // Ripples + haptics on every button and link
        installTouchFx();
        installScrollCalm();
    }, []);

    return (
        <I18nProvider>
            <LivingProvider>
            <ThemeProvider>
                <AuthProvider>
                    <BrowserRouter>
                        <div className="App min-h-screen bg-brand-bg text-brand-ink botanical-noise transition-colors duration-500">
                            <PageCanonical />
                            <LivingBackdrop />
                            <AmbientSync />
                            <AnalyticsPageViews />
                            <OfflineNotice />
                            <AppRouter />
                            <DeepLinkHandler />
                            <CalendarReminderGate />
                            <ErrorMonitor />
                            <CookieBanner />
                            <LanguageNudge />
                            <UpdateNotifier />
                            <Toaster richColors position="top-center" />
                            <AiCostDialog />
                        </div>
                    </BrowserRouter>
                </AuthProvider>
            </ThemeProvider>
            </LivingProvider>
        </I18nProvider>
    );
}

export default App;
